import { instance, ServiceResponse } from './Axios';

export interface GetFilesResponse {
  items: AttachmentFile[];
  page: number;
  lastPage: number;
}

export const exportCandidatesReport = async (params: {
  fullName?: string;
  skills?: string[];
  areaId?: number;
  englishLevel?: string;
  fromJobAssignedDate?: string;
  toJobAssignedDate?: string;
}) => {
  const searchParams = new URLSearchParams({
    fullName: params.fullName ?? '',
    areaId: params.areaId?.toString() ?? '',
    englishLevel: params.englishLevel ?? '',
    fromJobAssignedDate: params.fromJobAssignedDate ?? '',
    toJobAssignedDate: params.toJobAssignedDate ?? '',
  });

  if (params.skills) {
    params.skills.forEach((skill) => searchParams.append('skills', skill));
  }

  const response = await instance.get(`/reports/candidates?${searchParams.toString()}`, {
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'candidates.xlsx');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getCandidates = async (params: {
  page: number;
  count: number;
  fullName?: string;
  skills?: string[];
  areaId?: number;
  englishLevel?: string;
  fromJobAssignedDate?: string;
  toJobAssignedDate?: string;
}) => {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    count: params.count.toString(),
    fullName: params.fullName ?? '',
    areaId: params.areaId?.toString() ?? '',
    englishLevel: params.englishLevel ?? '',
    fromJobAssignedDate: params.fromJobAssignedDate ?? '',
    toJobAssignedDate: params.toJobAssignedDate ?? '',
  });

  if (params.skills) {
    params.skills.forEach((skill) => searchParams.append('skills', skill));
  }

  const url = `/candidates?${searchParams.toString()}`;

  const response = await instance.get<ServiceResponse<Candidate[]>>(url);
  return response.data;
};

export const createCandidate = async (data: Candidate) => {
  const response = await instance.post<Candidate>('/candidates', data);
  return response?.data;
};

export const isExistingEmail = async (email: string) => {
  const response = await instance.get<boolean>(`/candidates/${email}/email`);
  return response?.data;
};

export const isExistingPhone = async (phone: string) => {
  const response = await instance.get<boolean>(`/candidates/${phone}/phone`);
  return response?.data;
};

export const getCandidateById = async (id?: string) => {
  const response = await instance.get<Candidate>(`/candidates/${id}`);
  return response.data;
};

export const editCandidate = async (id?: string, data?: Candidate) => {
  const response = await instance.put(`/candidates/${id}`, data);
  return response.data;
};

export const uploadCandidateFile = async (id?: string, file?: FormData, documentType?: number) => {
  const url = documentType 
    ? `/candidates/${id}/files/${documentType}`
    : `/candidates/${id}/files/1`; // Default to Common (1) if not specified
  const response = await instance.post(url, file);
  return response.data;
};

export const getCandidateFiles = async (id: string, params: { page: number; count: number }) => {
  const response = await instance.get<GetFilesResponse>(`/candidates/${id}/files`, { params });
  return response.data.items;
};

export const getCandidateSkills = async (id: number) => {
  const response = await instance.get(`/candidates/${id}/skills`);
  return response.data;
};

export const editCandidateSkills = async (id?: string, data?: string[]) => {
  const response = await instance.put(`/candidates/${id}/skills`, { skills: data });
  return response.data;
};

export const editCandidateLanguageProficiency = async (id?: string, data?: LanguageProficiencyDataOut) => {
  const response = await instance.put(`/candidates/${id}/language-proficiency`, data);
  return response.data;
};

export const createCandidateNote = async (
  id: string,
  params: { description: string; userId: number },
) => {
  const response = await instance.post(`/candidates/${id}/notes`, params);
  return response.data;
};

export const getCandidateReferrals = async () => {
  const response = await instance.get('/candidates/referral-sources');
  return response.data;
};

export const getCandidateAreas = async () => {
  const response = await instance.get('/candidates/areas-sources');
  return response.data;
};

export const getCandidateCountries = async () => {
  const response = await instance.get('/candidates/countries-sources');
  return response.data;
};

